import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { getCategoryById } from '../config/magazineConfig';
import { getInstitution, getInstitutionTailwind } from '../config/institutionConfig';
import PdfCoverThumbnail from '../components/PdfCoverThumbnail';

const CategoryPage = () => {
  const { categoryId } = useParams();
  const category = getCategoryById(categoryId);
  const institution = getInstitution();
  const tw = getInstitutionTailwind();
  const colors = institution.colors;

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-8">
        <div className="bg-white rounded-2xl shadow-sm p-12 text-center max-w-md border border-gray-200">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-50 rounded-full mb-6">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">Categoría no encontrada</h1>
          <p className="text-gray-500 mb-6">La categoría que buscas no está disponible o no existe.</p>
          <Link
            to="/"
            className="inline-block px-6 py-3 text-white rounded-lg transition-all hover:opacity-90 shadow-md"
            style={{ background: `linear-gradient(135deg, ${colors.accentFrom}, ${colors.accentTo})` }}
          >
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${colors.headerGradientStart}, ${colors.headerGradientMid}, ${colors.headerGradientEnd})`
        }} />
        <div className="absolute inset-0 bg-pattern opacity-10" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="flex items-center gap-5">
            <div className="flex-shrink-0">
              <img
                src={`${process.env.PUBLIC_URL}${institution.logo}`}
                alt={`Logo ${institution.shortName}`}
                className="h-14 sm:h-16 drop-shadow-lg"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight drop-shadow-md">
                {category.name}
              </h1>
              <p className="mt-1.5 text-white/85 text-sm sm:text-base font-light">
                {category.description}
              </p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" className="w-full h-auto block">
            <path d="M0 60V30C240 0 480 0 720 30C960 60 1200 60 1440 30V60H0Z" fill="#F9FAFB" />
          </svg>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Breadcrumb */}
        <nav className="mb-8 flex items-center text-sm">
          <Link to="/" className="font-medium transition-colors hover:opacity-80" style={{ color: colors.ctaText }}>
            Centro de Publicaciones
          </Link>
          <svg className="h-4 w-4 mx-2 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <span className="text-gray-500">{category.name}</span>
        </nav>

        <div className="mb-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Publicaciones Disponibles
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            Haz clic en una publicación para visualizarla de manera interactiva
          </p>
        </div>

        {category.magazines.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: `${colors.primary}15` }}>
              <svg className="h-8 w-8 flex-shrink-0" fill="none" stroke={colors.primary} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No hay publicaciones disponibles</h3>
            <p className="text-gray-500">Aún no hay publicaciones en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
            {category.magazines.map((magazine) => (
              <Link
                key={magazine.id}
                to={`/${categoryId}/${magazine.id}`}
                className="group flex flex-col bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden border border-gray-100 hover:border-gray-300 hover:-translate-y-1"
              >
                {/* Cover — portrait aspect, full cover visible (Issuu style) */}
                <div
                  className="relative w-full overflow-hidden flex-shrink-0"
                  style={{
                    aspectRatio: '3 / 4',
                    background: `linear-gradient(135deg, ${colors.cardGradientFrom}, ${colors.cardGradientVia}, ${colors.cardGradientTo})`
                  }}
                >
                  {/* Fallback icon — shows while loading or on PDF error */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <svg
                      className="h-10 w-10 text-white/50 group-hover:text-white/70 transition-colors duration-200"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  {/* PDF cover thumbnail */}
                  <PdfCoverThumbnail pdfUrl={magazine.pdfUrl} />
                </div>

                {/* Info */}
                <div className="p-2.5 flex flex-col gap-0.5">
                  <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug">
                    {magazine.description}
                  </p>
                  <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                    {magazine.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CategoryPage;
